import { Dayjs } from 'dayjs';
import { Option } from 'src/pages/CaseDetails/interfaces/EmiSchedule.type';
import { ICustomPopup } from '../types';
import { IOption } from '../../ExternalDashboardSensei/types';
import { Filter } from '../components/PinFilters/interfaces';

export interface ILiveLocationTrackerSlice {
  filterTypes: IFilterTypes;
  filterData: Record<string, IFilterDataItem>;
  agentLocations: IAgentLocation[] | null;
  showAllInfoWindows: boolean;
  loadingFilters: boolean;
  showBlurMap: boolean;
  lastUpdatedAt: Date | string;
  selectedAgent: IAgentLocation | null;
  mapLocations: IAgentLocation[] | null;
  agentLocationHistory: { data: IAgentLocationHistory[]; loading: boolean };
  agentAllocations: {
    data: Record<string, IAgentAllocation> | null;
    loading: boolean;
    allocationsList: IOption[];
  };
  selectedFeedback: ISelectedFeedback;
  isAccordionExpanded: boolean;
  disabledFilters: Record<string, boolean>;
  agentLocationsLoading: boolean;
  caseFilters: {
    loading: boolean;
    filters: Filter[];
  };
}

export interface ISelectedFeedback {
  lanNo: string;
  interactionId: string;
  feedbackLocation: ILocation | null;
  caseLocation: ILocation | null;
  isSuspicious: boolean;
}

export interface IFeedback {
  interactionId: string;
  interactionStatus: string;
  suspicious: boolean;
  epochTimestamp: number;
  location: ILocation;
  accountNumber: string;
  caseReferenceId: string;
  businessVertical: string;
}

export interface IAgentAllocation {
  customerId: string;
  dpdBucket: string;
  totalOverdueAmount: string;
  customerName: string;
  location: ILocation;
  feedbacks: IFeedback[];
  addressText: string;
  caseReferenceId: string;
  businessVertical: string;
}

export interface ILocation {
  latitude: number;
  longitude: number;
}

export interface IAgentLocationHistory {
  referenceId: string;
  location: ILocation;
  epochTimestamp: number;
  zIndex?: number;
  recentLocation?: boolean;
}

export interface IAgentLocation {
  referenceId: string;
  name: string;
  phoneNumber: string;
  location: ILocation;
  activityLevel: number;
  lastUpdatedAt: string;
  zIndex: number;
  type?: LOCATION_TYPE_FILTERS;
  lanNo?: string;
  interactionId?: string;
  interactionStatus?: string;
  addressText?: string;
  totalAllocatedCases: number;
  recentLocation?: boolean;
  isTrainingAgent?: boolean;
}

interface IFilterTypes {
  filters: string[];
  loadOnMount: boolean;
}

interface IFilterDataItem {
  data: Option[];
  selectedValue: string | null;
  dependentFilter?: FilterTypes;
  nextFilter?: FilterTypes;
}

export interface MapPinLocation {
  type: string;
  location: ILocation;
  lastUpdatedAt?: string | Dayjs;
  name?: string;
  lanNo?: string;
  interactionId?: string;
  interactionStatus?: string;
  referenceId?: string;
  addressText?: string;
  zIndex?: number;
  recentLocation?: boolean;
}

export enum FilterTypes {
  AGENCY = 'AGENCY',
  TEAM_LEAD = 'TEAM_LEAD',
  AGENTS = 'AGENTS',
  LOCATION_TYPE = 'LOCATION_TYPE'
}

interface IFilterMapItem {
  placeholder: string;
  dependentOnFilter?: FilterTypes;
  nextFilter?: FilterTypes;
}

export interface IFilterMap {
  [key: string]: IFilterMapItem;
}

export interface IFilterPayload {
  type: FilterTypes;
  agencyCodes: string[];
}

export interface IAgentLocationPayload {
  agencyCodes: string[];
  teamLeadReferenceIds: string[];
}

export interface IAgentLocationHistoryPayload {
  date: string;
  referenceId: string;
}

export interface IAgentAllocationPayload {
  date: string;
  referenceId: string;
}

export enum AgentActivityLevel {
  HIGH,
  MEDIUM,
  LOW
}

export type ColorMap = Record<
  string,
  { shade1: string; shade2: string; shade3: string; textColor: string }
>;

export interface AgentMarkerProps {
  position: {
    lat: number;
    lng: number;
  };
  activeCustomPopup: React.MutableRefObject<ICustomPopup | null>;
  agentLocation: IAgentLocation;
}

export enum LOCATION_TYPE_FILTERS {
  ALL = 'ALL',
  CASES = 'CASES',
  AGENT = 'AGENT',
  GENUINE_FEEDBACK = 'GENUINE_FEEDBACK',
  SUSPICIOUS_FEEDBACK = 'SUSPICIOUS_FEEDBACK'
}

export interface AgentDetailProps {
  item: IAgentLocation;
  showNextIcon?: boolean;
  containerStyle?: string;
}

export interface FeedbackDetailProps {
  feedbacksList: IFeedback[];
  feedbackClickHandler: (feedback: IFeedback) => void;
}

export interface AgentDetailsPayload {
  date: string;
  referenceId: string;
}
